<template>
  <div class="parking-information-page">
    <main class="parking-information-shell">
      <header class="page-header">
        <button
          type="button"
          class="header-title"
          :aria-label="`${$t('message.ParkingInformation')}. Back to search`"
          @click="BackToMain"
        >
          {{ $t("message.ParkingInformation") }}
        </button>
        <p class="header-subtitle">
          {{ $t("message.ParkingDetail") }}
        </p>
      </header>

      <section class="information-section">
        <v-tabs v-model="tab" class="information-tabs" hide-slider>
          <v-tab @click="ChangeTab('Information')">{{
            $t("message.Information")
          }}</v-tab>
          <v-tab @click="ChangeTab('History')">{{
            $t("message.RedemptionHistory")
          }}</v-tab>
          <v-tab-item>
            <v-card flat class="tab-panel">
              <v-card-text class="information-content">
                <div class="ticket-card">
                  <table class="ticket-table">
                    <tbody>
                      <tr class="ticket-row ticket-row--primary">
                        <th scope="row">
                          {{ $t("message.TicketNo") }}
                        </th>
                        <td>
                          {{ TicketNo }}
                        </td>
                      </tr>
                      <tr class="ticket-row">
                        <th scope="row">
                          {{ $t("message.DateTime") }}
                        </th>
                        <td class="ticket-date">
                          {{ ChangeFormatDateOnly(DateTime) }}
                        </td>
                      </tr>
                      <tr class="ticket-row">
                        <th scope="row">
                          {{ $t("message.TimeIn") }}
                        </th>
                        <td>
                          {{ TimeIn }}
                        </td>
                      </tr>
                      <tr class="ticket-row">
                        <th scope="row">
                          {{ $t("message.Duration") }}
                        </th>
                        <td>
                          {{ formatDuration(Duration) }}
                        </td>
                      </tr>
                      <tr class="ticket-row">
                        <th scope="row">
                          {{ $t("message.CarLicense") }}
                        </th>
                        <td>
                          {{ CarLicense }}
                        </td>
                      </tr>
                      <tr class="ticket-row">
                        <th scope="row">
                          {{ $t("message.VehicleName") }}
                        </th>
                        <td>
                          {{
                            VehicleName == "Car"
                              ? $t("message.Car")
                              : $t("message.Motorcycle")
                          }}
                        </td>
                      </tr>
                      <tr class="ticket-row">
                        <th scope="row">
                          {{ $t("message.RateDetail") }}
                        </th>
                        <td class="rate-detail">
                          {{
                            $t("message.RateDetailLang") == "ENG"
                              ? RateDetailENG.split("\r\n")[0]
                              : RateDetail.split("\r\n")[0]
                          }}
                        </td>
                      </tr>
                      <tr class="ticket-row ticket-row--total">
                        <th scope="row">
                          {{ $t("message.Total") }}
                        </th>
                        <td>
                          {{ numberWithCommas(Total) }}
                          <span>
                            {{ $t("message.Baht") }}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </v-card-text>
            </v-card>
          </v-tab-item>
          <v-tab-item>
            <v-card flat class="tab-panel">
              <v-card-text class="history-content">
                <v-data-table
                  :headers="headersHistory"
                  :items="HistoryList"
                  :items-per-page="5"
                  :mobile-breakpoint="0"
                  single-line
                  hide-details
                  :disable-sort="true"
                >
                  <template v-slot:item="{ item }">
                    <tr>
                      <td style="text-align: center;">{{ item.rateCode }}</td>
                      <td>{{ item.detail }}</td>
                      <td>{{ ChangeFormatDate(item.stampTime) }}</td>
                      <td style="text-align: center;">{{ item.stampBy }}</td>
                    </tr>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-tab-item>
        </v-tabs>

        <div class="redemption-form">
          <section class="payment-fields" aria-label="Redemption values">
            <div class="payment-field">
              <h2 class="form-label">
                {{ $t("message.SpendingValue") }}
              </h2>
              <v-text-field
                v-model="SpendingValue"
                class="payment-input"
                type="number"
                min="0"
                inputmode="decimal"
                outlined
                hide-details
              ></v-text-field>
            </div>

            <!--
            <div class="payment-field">
              <h2 class="form-label">
                {{ $t("message.POSValue") }}
              </h2>
              <v-text-field
                v-model="POSValue"
                class="payment-input"
                outlined
                disabled
                hide-details
              ></v-text-field>
            </div>
            -->

            <div class="payment-field">
              <h2 class="form-label">
                {{ $t("message.CouponValue") }}
              </h2>
              <div class="coupon-control">
                <v-text-field
                  ref="couponInput"
                  v-model="CouponValue"
                  class="payment-input coupon-input"
                  outlined
                  disabled
                  hide-details
                ></v-text-field>
                <v-btn
                  class="scanner-action"
                  depressed
                  type="button"
                  @click="OpenScannerDialog"
                >
                  <img
                    class="scanner-icon"
                    src="@/assets/icons/scan_icon.png"
                    alt=""
                    aria-hidden="true"
                  />
                  {{ $t("message.Scanner") }}
                </v-btn>
              </div>
            </div>
          </section>

          <div
            v-if="UsagePOSValue > 0 || UsageCouponValue > 0"
            class="promotion-summary"
            role="status"
          >
            <span v-if="UsagePOSValue > 0">
              {{ $t("message.POSValue") }}: {{ UsagePOSValue }}
              {{ $t("message.Hour") }}
            </span>
            <span v-if="UsageCouponValue > 0">
              {{ $t("message.CouponValue") }}: {{ UsageCouponValue }}
              {{ $t("message.Hour") }}
            </span>
          </div>

          <section
            v-if="!hasValueBasedRedemption"
            class="form-section rate-section"
            @click="ClearRate()"
          >
            <h2 class="form-label">
              {{ $t("message.RateDetail") }}
            </h2>
            <v-radio-group v-model="RateData" class="rate-options" hide-details>
              <v-card
                v-for="(item, index) in RateList"
                :key="index"
                flat
                class="rate-option"
              >
                <v-radio
                  :value="item.rateCode"
                  hide-details
                  class="rate-radio pa-0"
                >
                  <template #label>
                    <span v-if="item.limitStatus">
                      {{ item.rateDetail }} (คงเหลือ
                      <span class="quota-remain">{{
                        item.limitQuotaRemain
                      }}</span>
                      จาก {{ item.limitQuota }} ครั้ง)
                    </span>
                    <span v-else>
                      {{ item.rateDetail }}
                    </span>
                  </template>
                </v-radio>
              </v-card>
            </v-radio-group>
          </section>

          <section class="form-section remark-section">
            <h2 class="form-label">
              {{ $t("message.Remark") }}
            </h2>
            <v-textarea
              v-model="Remark"
              class="remark-input"
              outlined
              hide-details
              rows="3"
            ></v-textarea>
          </section>

          <div class="form-actions">
            <v-btn
              v-if="RedeemButtonStatus == false"
              class="primary-action"
              depressed
              @click="RedeemData()"
            >
              {{ $t("message.EStamp") }}
            </v-btn>
            <v-btn
              v-else
              disabled
              class="primary-action"
              depressed
              @click="RedeemData()"
            >
              {{ $t("message.EStamp") }}
            </v-btn>
            <v-btn class="secondary-action" depressed @click="CancelRedeem()">
              {{ $t("message.Cancel") }}
            </v-btn>
          </div>
        </div>
      </section>
    </main>

    <v-dialog
      v-model="ScannerDialog"
      persistent
      max-width="884"
      @keydown.esc="CloseScannerDialog"
    >
      <v-card class="scanner-dialog">
        <v-card-title class="scanner-dialog__title">
          {{ $t("message.Scanner") }}
        </v-card-title>

        <v-card-text class="scanner-dialog__content">
          <div class="camera-preview">
            <qrcode-stream
              v-if="ScannerDialog && ScannerActive"
              :constraints="ScannerConstraints"
              :track="DrawScannerLine"
              @decode="OnScannerDecode"
              @init="OnScannerInit"
            />
            <div v-else class="camera-placeholder">
              {{ ScannerError || $t("message.ScanQRCode") }}
            </div>
          </div>

          <div class="scanner-entry">
            <v-text-field
              v-model.trim="TempCouponValue"
              class="scanner-entry__input"
              outlined
              hide-details
              :disabled="ScannerProcessing"
              @keyup.enter="ProcessScannedValue"
            ></v-text-field>
            <v-btn
              class="scanner-add-action"
              depressed
              :loading="ScannerProcessing"
              :disabled="!TempCouponValue || ScannerProcessing"
              @click="ProcessScannedValue"
            >
              {{ $t("message.ScannerAdd") }}
            </v-btn>
          </div>

          <div v-if="PendingTigerReceipt" class="tiger-amount-entry">
            <div class="tiger-amount-entry__detail">
              <span>SN: {{ PendingTigerReceipt.sn }}</span>
              <span>
                ยอดคงเหลือ:
                {{ numberWithCommas(TigerAvailableAmount) }}
                {{ $t("message.Baht") }}
              </span>
            </div>
            <v-text-field
              v-model="TempPOSValue"
              type="number"
              min="1"
              :max="TigerAvailableAmount"
              inputmode="decimal"
              outlined
              hide-details
              label="ยอดที่ต้องการใช้"
              :disabled="ScannerProcessing"
              @keyup.enter="AddPendingTigerReceipt"
            ></v-text-field>
            <v-btn
              class="scanner-add-action"
              depressed
              :loading="ScannerProcessing"
              :disabled="ScannerProcessing || !TempPOSValue"
              @click="AddPendingTigerReceipt"
            >
              {{ $t("message.ScannerAdd") }}
            </v-btn>
          </div>

          <v-data-table
            class="scanner-table"
            :headers="scannerHeaders"
            :items="ScannerList"
            :items-per-page="5"
            item-key="id"
            hide-default-footer
            disable-sort
          ></v-data-table>

          <div class="scanner-summary">
            <div class="scanner-summary__item">
              <span class="scanner-summary__label">
                {{ $t("message.ScannerTotal") }}
              </span>
              <strong class="scanner-summary__value">
                {{ numberWithCommas(TotalCouponAmount || 0) }}
                {{ $t("message.Baht") }}
              </strong>
            </div>
            <div class="scanner-summary__item">
              <span class="scanner-summary__label">
                {{ $t("message.ScannerTotalCoupon") }}
              </span>
              <strong class="scanner-summary__value">
                {{ UsageCouponValue || 0 }} {{ $t("message.Hour") }}
              </strong>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="scanner-dialog__actions">
          <v-btn class="scanner-cancel-action" depressed @click="CloseScannerDialog">
            {{ $t("message.Cancel") }}
          </v-btn>
          <v-btn
            class="scanner-submit-action"
            depressed
            :disabled="
              ScannerProcessing ||
              PendingTigerReceipt !== null ||
              ScannerList.length === 0
            "
            @click="SubmitScannedPromotions"
          >
            {{ $t("message.ScannerSubmit") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="LoadingDialog" persistent width="0.01">
      <v-card>
        <v-card-text>
          <v-row>
            <LoadingComponent class="centered"></LoadingComponent>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import axios from "axios";
import enurl from "@/api/environment";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { QrcodeStream } from "vue-qrcode-reader";
import LoadingComponent from "@/components/Loading";

export default {
  name: "ParkingInformation",
  components: {
    QrcodeStream,
    LoadingComponent,
  },
  data() {
    return {
      url: enurl.apiUrl,
      LoadingDialog: false,
      AlertDialog: false,
      TypeAlert: null,
      MessageAlert: "",
      id: null,
      response: "",
      RedemptionType: null,
      TenantID: null,
      LogID: null,
      TerminalID: null,
      DatetimeIn: "",
      MemberType: null,
      CarType: null,
      UserID: null,

      TicketNo: "",
      DateTime: "",
      TimeIn: "",
      Duration: "",
      exempt: "",
      summaryDuration: "",
      CarLicense: "",
      VehicleName: "",
      RateCodeString: "",
      RateDetail: "",
      RateDetailENG: "",
      Total: "",
      SpendingValue: "",
      RateList: [],
      RateData: null,
      OldRateData: null,
      Remark: "",
      ColorTab: "#126496",
      ColorText: "#126496",
      Status: false,
      tab: null,
      HistoryList: [],

      QRCodeData: "",
      idqr: null,
      POSValue: "",
      TempPOSValue: "",
      ScannerDialog: false,
      ScannerActive: false,
      ScannerProcessing: false,
      ScannerError: "",
      ScannerConstraints: { facingMode: "environment" },
      ScannerList: [],
      TempScannerList: [],
      PendingTigerReceipt: null,
      TigerAvailableAmount: 0,
      TotalAmountPOS: 0,
      UsagePOSValue: 0,

      TempSNScanDetail: "",
      TempAmountScanDetail: "",

      StatusReader: false,
      AddPOSButtonStatus: false,
      RedeemButtonStatus: false,

      CouponValue: "",
      TempCPNo: "",
      TempCPAmount: "",
      TempCouponValue: "",
      TotalCouponAmount: null,
      UsageCouponValue: 0,
      ResponseQRTigerText: "",
      ResponseData: [],
      RefLog: "",

      projectID: localStorage.getItem("ProjectSiteID"),
    };
  },
  computed: {
    hasValueBasedRedemption() {
      return (
        Number(this.SpendingValue) > 0 ||
        Number(this.POSValue) > 0 ||
        Number(this.CouponValue) > 0
      );
    },

    scannerHeaders() {
      return [
        { text: "CarparkID", value: "carparkId", align: "left" },
        { text: "SerialNumber", value: "serialNumber", align: "left" },
        { text: "Branch", value: "branch", align: "left" },
        { text: "Amount", value: "amount", align: "left" },
        { text: "StampDate", value: "stampDate", align: "left" },
        { text: "Type", value: "type", align: "left" },
        { text: "Status", value: "status", align: "left" },
      ];
    },

    headersHistory() {
      return [
        {
          text: "RateCode",
          align: "left",
        },
        {
          text: "Detail",
          align: "left",
        },
        {
          text: "StampTime",
          align: "left",
        },
        {
          text: "StampBy",
          align: "left",
        },
      ];
    },
  },

  async mounted() {
    let self = this;
    self.response = self.$route.params.response || [];
    if (self.response.length > 0) {
      self.LogID = self.$route.params.response[0].logId;
      self.TenantID = parseInt(localStorage.getItem("TenantID"));
    }
    await self.BindingDataResponse();
    if (self.LogID) {
      try {
        await self.RefreshPromotionData();
      } catch (error) {
        Swal.fire({
          icon: "error",
          width: 700,
          text: self.GetApiErrorMessage(error),
        });
      }
    }
  },

  methods: {
    async OpenScannerDialog() {
      this.ScannerError = "";
      this.TempCouponValue = "";
      this.PendingTigerReceipt = null;
      this.TigerAvailableAmount = 0;
      this.TempPOSValue = "";
      this.ScannerDialog = true;
      this.ScannerProcessing = true;
      try {
        await this.RefreshPromotionData();
        this.$nextTick(() => {
          this.ScannerActive = true;
        });
      } catch (error) {
        this.ScannerError = this.GetApiErrorMessage(error);
      } finally {
        this.ScannerProcessing = false;
      }
    },

    async CloseScannerDialog() {
      this.ScannerActive = false;
      this.ScannerProcessing = true;
      try {
        await Promise.all([
          this.CancelTigerReceiptRedeem(),
          this.CancelCouponRedeem(),
        ]);
        this.ResetPromotionState();
        this.ScannerDialog = false;
        this.ScannerError = "";
        this.TempCouponValue = "";
        this.PendingTigerReceipt = null;
        this.TigerAvailableAmount = 0;
        this.TempPOSValue = "";
      } catch (error) {
        await Swal.fire({
          icon: "error",
          width: 700,
          text: this.GetApiErrorMessage(error),
        });
      } finally {
        this.ScannerProcessing = false;
      }
    },

    DrawScannerLine(ctx, frame) {
      const { width, height } = frame;
      ctx.clearRect(0, 0, width, height);
      ctx.strokeStyle = "#f04424";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(0, height / 2);
      ctx.lineTo(width, height / 2);
      ctx.stroke();
    },

    async OnScannerInit(promise) {
      try {
        await promise;
      } catch (error) {
        this.ScannerActive = false;
        this.ScannerError =
          "ไม่สามารถเปิดกล้องได้ กรุณาตรวจสอบ Camera permission และ HTTPS";
      }
    },

    OnScannerDecode(value) {
      if (!value) return;
      this.TempCouponValue = value;
      this.ScannerActive = false;
    },

    async ProcessScannedValue() {
      const value = this.TempCouponValue.trim();
      if (!value || this.ScannerProcessing) return;

      this.ScannerProcessing = true;
      this.ScannerActive = false;
      this.ScannerError = "";
      try {
        if (value.substring(0, 3).toUpperCase() === "PMS") {
          await this.AddCouponByQRCode(value);
        } else {
          await this.AddTigerByQRCode(value);
        }
        this.TempCouponValue = "";
        if (!this.PendingTigerReceipt) {
          await this.RefreshPromotionData();
        }
      } catch (error) {
        const message = this.GetApiErrorMessage(error);
        this.ScannerError = message;
        await Swal.fire({ icon: "error", width: 700, text: message });
      } finally {
        this.ScannerProcessing = false;
        if (this.ScannerDialog && !this.PendingTigerReceipt) {
          this.$nextTick(() => {
            this.ScannerActive = true;
          });
        }
      }
    },

    async AddCouponByQRCode(qrData) {
      const couponType = Number(this.CarType) === 0 ? 1 : 2;
      const couponResponse = await axios.post(
        `${this.url}Redemption/getCouponByQRCode`,
        { QRData: qrData, CouponType: couponType },
      );
      const coupon = couponResponse.data.data;
      if (!coupon || !coupon.couponNumber) {
        throw new Error(couponResponse.data.message || "ไม่พบข้อมูลคูปอง");
      }

      await axios.post(`${this.url}Redemption/addCouponForRedeem`, {
        CouponNumber: coupon.couponNumber,
        Datetime: this.DatetimeIn,
        LogCarparkID: this.LogID,
        TenantID: this.TenantID,
        CouponType: couponType,
      });
    },

    async AddTigerByQRCode(qrData) {
      const tigerResponse = await axios.post(
        `${this.url}Redemption/getTigerReceiptByQRCode`,
        { QRData: qrData },
      );
      const receipt = tigerResponse.data.data;
      if (!receipt || !receipt.sn || Number(receipt.amt) <= 0) {
        throw new Error(tigerResponse.data.message || "ข้อมูลใบเสร็จไม่ถูกต้อง");
      }

      this.PendingTigerReceipt = receipt;
      this.TigerAvailableAmount = Number(receipt.amt);
      this.TempPOSValue = String(receipt.amt);
    },

    async AddPendingTigerReceipt() {
      if (!this.PendingTigerReceipt || this.ScannerProcessing) return;

      const amount = Number(this.TempPOSValue);
      if (
        !Number.isFinite(amount) ||
        amount <= 0 ||
        amount > this.TigerAvailableAmount
      ) {
        await Swal.fire({
          icon: "warning",
          width: 650,
          text: `ยอดที่ต้องการใช้ต้องอยู่ระหว่าง 1 ถึง ${this.numberWithCommas(
            this.TigerAvailableAmount,
          )} บาท`,
        });
        return;
      }

      this.ScannerProcessing = true;
      try {
        const receipt = this.PendingTigerReceipt;
        await axios.post(`${this.url}Redemption/addTigerReceiptForRedeem`, {
          SerialNumber: receipt.sn,
          Branch: receipt.bc || "",
          Datetime: receipt.dtr || "",
          Amount: amount,
          LogCarparkID: this.LogID,
          EntryDate: this.DatetimeIn,
          StationID: "99",
        });
        this.PendingTigerReceipt = null;
        this.TigerAvailableAmount = 0;
        this.TempPOSValue = "";
        this.ScannerError = "";
        await this.RefreshPromotionData();
      } catch (error) {
        const message = this.GetApiErrorMessage(error);
        this.ScannerError = message;
        await Swal.fire({ icon: "error", width: 700, text: message });
      } finally {
        this.ScannerProcessing = false;
        if (this.ScannerDialog && !this.PendingTigerReceipt) {
          this.$nextTick(() => {
            this.ScannerActive = true;
          });
        }
      }
    },

    async SubmitScannedPromotions() {
      if (this.TempCouponValue.trim()) {
        await this.ProcessScannedValue();
        if (this.ScannerError) return;
      }
      this.ScannerActive = false;
      this.ScannerDialog = false;
      this.TempCouponValue = "";
    },

    async RefreshPromotionData() {
      await Promise.all([
        this.GetPromotionTransactions(),
        this.GetPromotionTiger(),
        this.GetPromotionCoupon(),
      ]);
    },

    async GetPromotionTransactions() {
      try {
        const response = await axios.post(
          `${this.url}Redemption/getTigerReceiptRedeemUsed`,
          { LogCarparkID: this.LogID },
        );
        const items = response.data.data || [];
        this.ScannerList = items
          .filter((item) => item.statusApprove !== true)
          .map((item) => ({
            id: item.trn_Log_Pos_ID,
            carparkId: item.trn_Log_Carpark_ID,
            serialNumber: item.sale_Number,
            branch: item.branch || "-",
            amount: Number(item.amount) || 0,
            stampDate: this.ChangeFormatDate(item.stampDate),
            type: Number(item.type) === 1 ? "Tiger" : "Coupon",
            status: item.statusApprove === true ? "Approve" : "Pending",
          }));
      } catch (error) {
        this.ScannerList = [];
        throw error;
      }
    },

    async GetPromotionTiger() {
      try {
        const response = await axios.post(
          `${this.url}Redemption/getPromotionTiger`,
          { LogCarparkID: this.LogID },
        );
        const promotion = response.data.data || {};
        this.TotalAmountPOS = Number(promotion.totalAmount) || 0;
        this.POSValue = this.TotalAmountPOS > 0 ? String(this.TotalAmountPOS) : "";
        this.UsagePOSValue = Number(promotion.freeHour) || 0;
      } catch (error) {
        if (!error.response || error.response.status !== 404) throw error;
        this.TotalAmountPOS = 0;
        this.POSValue = "";
        this.UsagePOSValue = 0;
      }
    },

    async GetPromotionCoupon() {
      const response = await axios.post(
        `${this.url}Redemption/getPromotionCoupon`,
        { LogCarparkID: this.LogID },
      );
      const promotion = response.data.data || {};
      this.TotalCouponAmount = Number(promotion.totalCoupon) || 0;
      this.CouponValue =
        this.TotalCouponAmount > 0 ? String(this.TotalCouponAmount) : "";
      this.UsageCouponValue = Number(promotion.freeHour) || 0;
    },

    async CancelTigerReceiptRedeem() {
      try {
        await axios.post(`${this.url}Redemption/cancelTigerReceiptRedeem`, {
          LogCarparkID: this.LogID,
        });
      } catch (error) {
        if (!error.response || error.response.status !== 404) throw error;
      }
    },

    async CancelCouponRedeem() {
      await axios.post(`${this.url}Redemption/cancelCouponRedeem`, {
        LogCarparkID: this.LogID,
      });
    },

    ResetPromotionState() {
      this.ScannerList = [];
      this.TotalAmountPOS = 0;
      this.TotalCouponAmount = 0;
      this.POSValue = "";
      this.CouponValue = "";
      this.UsagePOSValue = 0;
      this.UsageCouponValue = 0;
      this.RateData = null;
      this.PendingTigerReceipt = null;
      this.TigerAvailableAmount = 0;
      this.TempPOSValue = "";
    },

    GetApiErrorMessage(error) {
      if (error && error.response && error.response.data) {
        return error.response.data.message || "เกิดข้อผิดพลาด";
      }
      return (error && error.message) || "เกิดข้อผิดพลาด";
    },

    ClearRate() {
      let that = this;
      if (that.OldRateData == that.RateData) {
        that.RateData = null;
      }
      that.OldRateData = that.RateData;
    },

    ChangeTab(value) {
      let self = this;
      if (value == "Information") {
        self.ColorTab = "#126496";
        self.ColorText = "#126496";
        self.Status = false;
      } else {
        self.GetDataRedemptionHistoryByLogId();
        self.ColorTab = "#126496";
        self.Status = true;
      }
    },

    GetDataRedemptionHistoryByLogId() {
      let self = this;
      let temp = {
        LogCarparkID: self.LogID,
      };
      axios
        .post(`${self.url}Redemption/GetDataRedemptionHistoryByLogId`, temp)
        .then(function (response) {
          if (response.data.status == 0) {
            self.HistoryList = response.data.data || [];
          }
        })
        .catch(function (error) {
          let message = "เกิดข้อผิดพลาด";
          if (error.response && error.response.data) {
            message = error.response.data.message || message;
          }
          Swal.fire({
            icon: "error",
            width: 900,
            text: message,
          });
        });
    },

    BackToMain() {
      if (this.LogID) {
        return this.CancelRedeem();
      }
      return this.$router.push({ name: "Main" });
    },

    async GetDataRateCode(membertype, vehicletype) {
      let self = this;
      let temp = {
        MemberType: membertype,
        VehicleType: vehicletype,
        projectID: self.projectID,
      };
      await axios
        .post(`${self.url}Redemption/GetDataRateCode`, temp)
        .then(function (response) {
          if (response.data.status == 0) {
            self.RateList = response.data.data;
          }
        })
        .catch(function (error) {
          let message = "เกิดข้อผิดพลาด";
          if (error.response && error.response.data) {
            message = error.response.data.message || message;
          }
          Swal.fire({
            icon: "error",
            width: 900,
            text: message,
          });
          self.BackToMain();
        });
    },

    async CancelRedeem() {
      this.LoadingDialog = true;
      try {
        await Promise.all([
          this.CancelTigerReceiptRedeem(),
          this.CancelCouponRedeem(),
        ]);
        this.ResetPromotionState();
        this.$router.push({ name: "Main" });
      } catch (error) {
        await Swal.fire({
          icon: "error",
          width: 700,
          text: this.GetApiErrorMessage(error),
        });
      } finally {
        this.LoadingDialog = false;
      }
    },

    async BindingDataResponse() {
      let self = this;
      // console.log(self.response);
      if (self.response != "" && self.response != undefined) {
        if (self.response.length > 0 && self.response[0].status != "Fail") {
          self.ResponseData = self.response[0];
          self.TerminalID = self.response[0].terminalInId;
          self.DatetimeIn = self.response[0].entryDateTime;
          self.MemberType = self.response[0].memberTypeId;
          self.CarType = self.response[0].vehicleTypeId;
          self.TicketNo = self.response[0].ticketNo;
          self.DateTime = new Date(
            self.response[0].entryDateTime,
          ).toLocaleDateString("en-us", {
            year: "numeric",
            month: "short",
            day: "numeric",
          });
          self.TimeIn =
            self.response[0].entryDateTime.split(" ").length > 0
              ? self.response[0].entryDateTime.split(" ")[1]
              : "";
          self.Duration =
            self.response[0].parkHH +
            " hrs " +
            self.response[0].parkMM +
            " mins";
          self.exempt =
            self.response[0].redeemHourDisplay +
            " hrs " +
            self.response[0].redeemMinuteDisplay +
            " mins";
          self.summaryDuration =
            self.response[0].parkHHDisplay +
            " hrs " +
            self.response[0].parkMMDisplay +
            " mins";
          self.CarLicense = self.response[0].plateNo;
          self.VehicleName = self.response[0].vehicleTypeName;
          self.RateCodeString = self.response[0].rateCode;
          self.RateDetail = self.response[0].rateDetailTH;
          self.RateDetailENG = self.response[0].rateDetailEN;
          self.Total = self.response[0].total;
          await this.GetDataRateCode(
            self.response[0].memberTypeId,
            self.response[0].vehicleTypeId,
          );
        } else {
          Swal.fire({
            icon: "error",
            width: 900,
            text: self.response[0].message,
          });
        }
      } else {
        self.BackToMain();
      }
    },

    numberWithCommas(x) {
      return Number(x || 0).toLocaleString();
    },

    RedeemData() {
      let self = this;
      if (
        self.LogID == "" ||
        self.TerminalID == null ||
        self.DatetimeIn == "" ||
        self.TicketNo == "" ||
        self.CarLicense == "" ||
        self.MemberType == null ||
        self.CarType == null ||
        self.TenantID == null
      ) {
        Swal.fire({
          icon: "warning",
          width: 600,
          text: "กรุณากรอกข้อมูลให้ครบถ้วน!",
        });
      } else if (
        !self.hasValueBasedRedemption &&
        (self.RateData == null || self.RateData === 0)
      ) {
        Swal.fire({
          icon: "warning",
          width: 600,
          text: "กรุณาเลือก Rate หรือระบุ Spending/Promotion",
        });
      } else if (
        self.SpendingValue !== "" &&
        (!Number.isFinite(Number(self.SpendingValue)) ||
          Number(self.SpendingValue) <= 0)
      ) {
        Swal.fire({
          icon: "warning",
          width: 600,
          text: "กรุณาระบุยอด Spending ให้ถูกต้อง",
        });
      } else {
        self.RedeemButtonStatus = true;
        self.LoadingDialog = true;
        let temp = {
          LogCarparkID: self.LogID,
          TerminalID: self.TerminalID,
          DatetimeIn: self.DatetimeIn,
          RunningNumber: self.TicketNo,
          PlateNumber: self.CarLicense,
          MemberType: self.MemberType,
          CarType: self.CarType,
          TenantID: self.TenantID,
          RateCode: self.RateData || 0,
          SpendingValue:
            self.SpendingValue === "" ? null : String(self.SpendingValue),
          POSValue: self.POSValue === "" ? null : String(self.POSValue),
          CouponValue:
            self.CouponValue === "" ? null : String(self.CouponValue),
          Remark: self.Remark,
          RefLog: self.RefLog,
          projectID: self.projectID,
        };

        axios
          .post(`${self.url}Redemption/RedeemParking`, temp)
          .then(function (response) {
            if (response.data.status == 0) {
              self.RedeemButtonStatus = false;
              self.$router.push({
                name: "Redemption",
                params: {
                  ResponseRedeem: response.data.data,
                  ratecode: self.RateData
                    ? (self.RateList.find(
                        (x) => x.rateCode == self.RateData,
                      ) || {}).rateDetail
                    : response.data.data,
                },
              });
            } else {
              Swal.fire({
                icon: "warning",
                width: 600,
                text: response.data.message || "ไม่สามารถทำ redemption ได้ กรุณาลองใหม่",
              });
            }
          })
          .catch(function (error) {
            self.RedeemButtonStatus = false;
            let message = "เกิดข้อผิดพลาด";
            if (error.response && error.response.data) {
              message = error.response.data.message || message;
            }
            Swal.fire({
              icon: "error",
              width: 900,
              text: message,
            });
          })
          .finally(() => {
            self.RedeemButtonStatus = false;
            self.LoadingDialog = false;
          });
      }
    },

    ChangeFormatDate(date) {
      if (!date) return null;

      const dateData = new Date(date);
      if (Number.isNaN(dateData.getTime())) return null;

      const thaiMonths = [
        "ม.ค",
        "ก.พ",
        "มี.ค",
        "เม.ย",
        "พ.ค",
        "มิ.ย",
        "ก.ค",
        "ส.ค",
        "ก.ย",
        "ต.ค",
        "พ.ย",
        "ธ.ค",
      ];
      const englishMonths = [
        "JAN",
        "FEB",
        "MAR",
        "APR",
        "MAY",
        "JUN",
        "JUL",
        "AUG",
        "SEP",
        "OCT",
        "NOV",
        "DEC",
      ];
      const months = this.$i18n.locale === "th" ? thaiMonths : englishMonths;
      const hours = String(dateData.getHours()).padStart(2, "0");
      const minutes = String(dateData.getMinutes()).padStart(2, "0");

      return `${dateData.getDate()} ${months[dateData.getMonth()]} ${dateData.getFullYear()}  ${hours}:${minutes}`;
    },

    ChangeFormatDateOnly(date) {
      if (!date) return null;
      const d = new Date(date);
      if (Number.isNaN(d.getTime())) return null;
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear() + 543;
      return `${day}/${month}/${year}`;
    },

    formatDuration(duration) {
      if (!duration) {
        return `0 ${this.$t("message.Hour")} 0 ${this.$t("message.Minute")}`;
      }
      const hours = parseInt(duration.match(/(\d+)\s*hrs?/i)?.[1] || 0);
      const minutes = parseInt(duration.match(/(\d+)\s*mins?/i)?.[1] || 0);
      return `${hours} ${this.$t("message.Hour")} ${minutes} ${this.$t(
        "message.Minute",
      )}`;
    },
  },
};
</script>
<style scoped>
.parking-information-page {
  min-height: calc(100vh - 64px);
  background: #ffffff;
  color: #262223;
}

.parking-information-shell {
  width: min(100% - 48px, 1320px);
  margin: 0 auto;
  padding: 20px 0 80px;
}

.page-header {
  text-align: center;
}

.header-title {
  padding: 0;
  color: #262223;
  background: transparent;
  border: 0;
  font-family: "Mrs Eaves Lining OT", "Times New Roman", serif !important;
  font-size: 36px;
  font-weight: 400;
  line-height: 1.15;
  letter-spacing: 0.02em;
  cursor: pointer;
}

.header-title:hover {
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 7px;
}

.header-title:focus-visible {
  outline: 2px solid #262223;
  outline-offset: 6px;
}

.header-subtitle {
  margin: 10px 0 0;
  color: #342f30;
  font-family: "Mrs Eaves Lining OT", "Times New Roman", serif !important;
  font-size: 20px;
  line-height: 1.3;
  letter-spacing: 0.025em;
}

.information-section {
  margin-top: 88px;
}

.information-tabs {
  background: transparent !important;
}

.information-tabs >>> .v-tabs-bar {
  height: 55px;
  background: transparent !important;
  border-bottom: 1px solid #969292;
}

.information-tabs >>> .v-slide-group__content {
  justify-content: flex-start;
}

.information-tabs >>> .v-tab {
  position: relative;
  flex: 0 0 auto;
  min-width: auto;
  max-width: none;
  height: 55px;
  padding: 0 60px;
  color: #262223 !important;
  font-family: "Mrs Eaves Lining OT", "Times New Roman", serif !important;
  font-size: 25px;
  font-weight: 400;
  letter-spacing: 0.01em;
}

.information-tabs >>> .v-tab + .v-tab {
  margin-left: 0;
}

.information-tabs >>> .v-tab::before {
  display: none;
}

.information-tabs >>> .v-tab--active::after {
  position: absolute;
  right: 0;
  bottom: -1px;
  left: 0;
  height: 5px;
  background: #262223;
  content: "";
}

.tab-panel {
  background: transparent !important;
}

.information-content,
.history-content {
  padding: 32px 5px 0 !important;
}

.history-content {
  padding-bottom: 18px !important;
}

.ticket-card {
  overflow: hidden;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 8px 8px rgba(38, 34, 35, 0.25);
}

.ticket-table {
  width: 100%;
  border-collapse: collapse;
  color: #262223;
  font-family: "Mrs Eaves Lining OT", "Times New Roman", serif !important;
  font-size: 20px;
  line-height: 1.25;
}

.ticket-row {
  height: 46px;
}

.ticket-row th,
.ticket-row td {
  width: 50%;
  padding: 7px 42px;
  border: 0;
  font-weight: 400;
}

.ticket-row th {
  text-align: left;
}

.ticket-row td {
  text-align: right;
}

.ticket-row--primary,
.ticket-row--total {
  height: 52px;
  background: #262223;
  color: #ffffff;
}

.ticket-date {
  text-transform: uppercase;
}

.rate-detail {
  color: #f04424;
}

.redemption-form {
  width: 100%;
  margin: 52px auto 0;
}

.payment-fields {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.35fr);
  gap: 28px;
  margin-bottom: 46px;
}

.payment-field {
  min-width: 0;
}

.payment-input >>> .v-input__slot {
  min-height: 65px;
  height: 65px;
  margin: 0;
  background: #ffffff !important;
  border-radius: 12px !important;
}

.payment-input {
  flex: 1 1 auto;
  width: 100%;
  height: 65px;
}

.payment-input >>> fieldset {
  border-color: #777273 !important;
}

.coupon-control {
  display: flex;
  align-items: stretch;
  gap: 8px;
}

.coupon-input {
  min-width: 0;
}

.promotion-summary {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px 28px;
  margin: -18px 0 38px;
  color: #342f30;
  font-size: 17px;
}

.scanner-action {
  width: 169px;
  min-width: 169px !important;
  height: 65px !important;
  border: 1px solid #777273;
  border-radius: 12px !important;
  background: #d8d6d6 !important;
  color: #262223 !important;
  font-family: "Mrs Eaves Lining OT", "Times New Roman", serif !important;
  font-size: 17px !important;
  font-weight: 400 !important;
  letter-spacing: 0.01em !important;
  text-transform: none !important;
}

.scanner-action:hover,
.scanner-action:focus-visible {
  background: #c7c4c5 !important;
}

.scanner-icon {
  width: 51px;
  height: 43px;
  margin-right: 14px;
  object-fit: contain;
}

.scanner-dialog {
  display: flex;
  width: 884px;
  max-width: 100%;
  height: 1026px;
  max-height: calc(100vh - 32px);
  flex-direction: column;
  overflow: hidden;
  border-radius: 14px !important;
  background: #ffffff;
}

.scanner-dialog__title {
  justify-content: center;
  min-height: 59px;
  padding: 9px 24px !important;
  background: #262223;
  color: #ffffff;
  font-family: "Mrs Eaves Lining OT", "Times New Roman", serif !important;
  font-size: 30px !important;
  font-weight: 400 !important;
}

.scanner-dialog__content {
  flex: 1 1 auto;
  max-height: none;
  overflow-y: auto;
  padding: 22px 37px 16px !important;
}

.camera-preview {
  position: relative;
  overflow: hidden;
  width: 810px;
  max-width: 100%;
  height: 325px;
  margin: 0 auto;
  border-radius: 14px;
  background: #000000;
}

.camera-preview >>> video,
.camera-preview >>> canvas {
  width: 100%;
  height: 325px;
  object-fit: cover;
}

.camera-placeholder {
  display: flex;
  height: 325px;
  align-items: center;
  justify-content: center;
  padding: 24px;
  color: #ffffff;
  font-size: 17px;
  text-align: center;
}

.scanner-entry {
  display: flex;
  gap: 13px;
  margin-top: 46px;
}

.scanner-entry__input {
  flex: 1 1 auto;
  width: auto;
  max-width: 100%;
  height: 30px;
  min-width: 0;
}

.scanner-entry__input >>> .v-input__slot {
  min-height: 45px;
  height: 45px;
  margin: 0;
  border-radius: 9px !important;
}

.scanner-entry__input >>> input {
  padding: 0;
}

.scanner-add-action {
  width: 120px;
  min-width: 120px !important;
  height: 45px !important;
  border-radius: 9px !important;
  background: #262223 !important;
  color: #ffffff !important;
  font-family: "Mrs Eaves Lining OT", "Times New Roman", serif !important;
  font-size: 18px !important;
}

.tiger-amount-entry {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(180px, 240px) 120px;
  gap: 13px;
  align-items: end;
  margin-top: 24px;
}

.tiger-amount-entry__detail {
  display: flex;
  min-height: 45px;
  flex-direction: column;
  justify-content: center;
  color: #342f30;
  font-size: 16px;
  line-height: 1.35;
}

.tiger-amount-entry >>> .v-input__slot {
  min-height: 45px;
  height: 45px;
  margin: 0;
  border-radius: 9px !important;
}

.scanner-table {
  min-height: 171px;
  margin-top: 52px;
  overflow: hidden;
  border-radius: 10px;
  box-shadow: 0 5px 7px rgba(38, 34, 35, 0.2);
}

.scanner-table >>> .v-data-table-header {
  background: #262223;
}

.scanner-table >>> .v-data-table-header th {
  color: #ffffff !important;
  padding: 0 10px !important;
  font-family: "Mrs Eaves Lining OT", "Times New Roman", serif !important;
  font-size: 14px !important;
  font-weight: 400 !important;
  letter-spacing: 0.01em;
  white-space: nowrap;
}

.scanner-table >>> .v-data-table-header tr {
  height: 32px;
}

.scanner-table >>> .v-data-table__wrapper {
  min-height: 171px;
}

.scanner-table >>> .v-data-table__wrapper {
  overflow-x: hidden;
}

.scanner-table >>> table {
  width: 100%;
  table-layout: auto;
}

.scanner-summary {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
  margin-top: 36px;
}

.scanner-summary__item {
  height: 82px;
  padding: 10px 22px 12px;
  border: 1px solid #777273;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 8px 8px rgba(38, 34, 35, 0.28);
  font-family: "Mrs Eaves Lining OT", "Times New Roman", serif;
}

.scanner-summary__label,
.scanner-summary__value {
  display: block;
}

.scanner-summary__label {
  color: #969292;
  font-size: 17px;
  line-height: 1.2;
}

.scanner-summary__value {
  margin-top: 10px;
  color: #262223;
  font-size: 23px;
  font-weight: 400;
  line-height: 1.2;
}

.scanner-dialog__actions {
  flex: 0 0 auto;
  justify-content: center;
  gap: 28px;
  padding: 36px 24px 58px !important;
}

.scanner-cancel-action,
.scanner-submit-action {
  width: 214px;
  min-width: 214px !important;
  height: 58px !important;
  border-radius: 999px !important;
  font-family: "Mrs Eaves Lining OT", "Times New Roman", serif !important;
  font-size: 19px !important;
  font-weight: 400 !important;
}

.scanner-cancel-action {
  background: #ffffff !important;
  color: #262223 !important;
  box-shadow: 0 7px 8px rgba(38, 34, 35, 0.24) !important;
}

.scanner-submit-action {
  background: #262223 !important;
  color: #ffffff !important;
}

.form-section + .form-section {
  margin-top: 28px;
}

.form-label {
  margin: 0 0 24px;
  color: #262223;
  font-family: "Mrs Eaves Lining OT", "Times New Roman", serif !important;
  font-size: 25px;
  font-weight: 400;
  letter-spacing: 0.01em;
}

.rate-section {
  width: min(100%, 1188px);
  margin-right: auto;
  margin-left: auto;
}

.rate-options {
  margin: 0 !important;
  padding: 0 !important;
}

.rate-option {
  min-height: 63px;
  margin-bottom: 18px;
  padding: 8px 28px;
  display: flex;
  align-items: center;
  background: #ffffff !important;
  border: 1px solid #4b4547;
  border-radius: 12px !important;
  box-shadow: 0 7px 7px rgba(38, 34, 35, 0.2) !important;
}

.rate-radio {
  width: 100%;
}

.rate-radio >>> .v-label {
  color: #342f30 !important;
  font-family: "Mrs Eaves Lining OT", "Times New Roman", serif !important;
  font-size: 18px;
  line-height: 1.35;
  letter-spacing: 0.015em;
}

.rate-radio >>> .v-input--selection-controls__input {
  width: 30px;
  height: 30px;
  margin-right: 42px;
}

.rate-radio >>> .v-input--selection-controls__input .v-icon {
  color: #342f30 !important;
  font-size: 30px;
}

.quota-remain {
  color: #d62f22;
}

.remark-input >>> .v-input__slot {
  background: #ffffff !important;
  border-radius: 12px !important;
}

.remark-section {
  width: min(100%, 780px);
  margin-right: auto;
  margin-left: auto;
}

.remark-input >>> fieldset {
  border-color: #777273 !important;
}

.form-actions {
  display: flex;
  justify-content: center;
  gap: 14px;
  margin-top: 40px;
}

.primary-action,
.secondary-action {
  min-width: 155px !important;
  height: 46px !important;
  padding: 0 24px !important;
  border-radius: 999px !important;
  font-family: "Mrs Eaves Lining OT", "Times New Roman", serif !important;
  font-size: 17px !important;
  font-weight: 400 !important;
  letter-spacing: 0.01em !important;
  text-transform: none !important;
}

.primary-action {
  background: #262223 !important;
  box-shadow: 0 8px 8px rgba(38, 34, 35, 0.28) !important;
  color: #ffffff !important;
}

.secondary-action {
  background: #ffffff !important;
  border: 1px solid #262223;
  color: #262223 !important;
}

.primary-action:hover,
.primary-action:focus-visible {
  background: #403a3c !important;
}

.history-content >>> .v-data-table {
  overflow: hidden;
  border: 1px solid #262223;
  border-radius: 14px;
  color: #262223;
  font-family: "Mrs Eaves Lining OT", "Times New Roman", serif !important;
  font-size: 16px;
  box-shadow: 0 8px 12px rgba(38, 34, 35, 0.32);
}

.history-content >>> .v-data-table-header {
  background: #262223;
}

.history-content >>> .v-data-table-header th {
  height: 48px;
  padding: 0 24px !important;
  color: #ffffff !important;
  font-family: "Mrs Eaves Lining OT", "Times New Roman", serif !important;
  font-size: 16px !important;
  font-weight: 400 !important;
  letter-spacing: 0.01em;
  white-space: nowrap;
}

.history-content >>> table {
  table-layout: fixed;
}

.history-content >>> th:nth-child(1),
.history-content >>> td:nth-child(1) {
  width: 10%;
}

.history-content >>> th:nth-child(2),
.history-content >>> td:nth-child(2) {
  width: 65%;
}

.history-content >>> th:nth-child(3),
.history-content >>> td:nth-child(3) {
  width: 16%;
  white-space: nowrap;
}

.history-content >>> th:nth-child(4),
.history-content >>> td:nth-child(4) {
  width: 9%;
  white-space: nowrap;
}

.history-content >>> tbody td {
  height: 52px;
  padding: 0 24px !important;
  border-bottom: 0 !important;
  font-size: 18px !important;
  line-height: 1.35;
}

.history-content >>> tbody tr:nth-child(even) {
  background: #ffffff;
}

.history-content >>> tbody tr:hover {
  background: #f8f7f7 !important;
}

.history-content >>> .v-data-table__wrapper {
  min-height: 196px;
}

.history-content >>> .v-data-footer {
  min-height: 64px;
  border-top: 0;
  font-size: 15px;
}

.history-content >>> .v-data-footer__select {
  margin-left: auto;
}

.history-content >>> .v-data-table-header-mobile {
  display: none;
}

.centered {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

@media (max-width: 959px) {
  .parking-information-shell {
    width: min(100% - 40px, 900px);
    padding-top: 48px;
  }

  .information-section {
    margin-top: 86px;
  }

  .ticket-row th,
  .ticket-row td {
    padding-right: 28px;
    padding-left: 28px;
  }

  .payment-fields {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 28px;
  }


  .payment-input {
    flex: 1 1 auto;
    width: 100%;
  }

}

@media (max-width: 600px) {
  .parking-information-page {
    min-height: calc(100vh - 56px);
  }

  .parking-information-shell {
    width: calc(100% - 32px);
    padding: 40px 0 72px;
  }

  .header-title {
    font-size: 34px;
  }

  .header-subtitle {
    margin-top: 10px;
    font-size: 19px;
  }

  .information-section {
    margin-top: 66px;
  }

  .information-tabs >>> .v-tabs-bar,
  .information-tabs >>> .v-tab {
    height: 48px;
  }

  .information-tabs >>> .v-tab {
    padding: 0 5px;
    font-size: 20px;
  }

  .information-tabs >>> .v-tab + .v-tab {
    margin-left: 8px;
  }

  .information-tabs >>> .v-tab--active::after {
    height: 4px;
  }

  .information-content,
  .history-content {
    padding: 30px 0 0 !important;
  }

  .history-content {
    padding-bottom: 18px !important;
  }

  .ticket-card {
    border-radius: 11px;
  }

  .ticket-table {
    font-size: 17px;
  }

  .ticket-row {
    height: auto;
  }

  .ticket-row th,
  .ticket-row td {
    padding: 12px 14px;
    vertical-align: top;
  }

  .rate-detail {
    overflow-wrap: anywhere;
  }

  .redemption-form {
    margin-top: 48px;
  }

  .payment-fields {
    grid-template-columns: 1fr;
    gap: 24px;
    margin-bottom: 38px;
  }


  .coupon-control {
    flex-direction: column;
  }

  .scanner-action {
    width: 100%;
    min-width: 0 !important;
    height: 52px !important;
  }

  .scanner-dialog__content {
    padding: 16px 16px 16px !important;
  }

  .scanner-dialog {
    width: 100%;
    height: auto;
    max-height: calc(100vh - 32px);
  }

  .scanner-table >>> .v-data-table__wrapper {
    overflow-x: auto;
  }

  .scanner-table >>> table {
    min-width: 720px;
  }

  .camera-preview,
  .camera-preview >>> video,
  .camera-preview >>> canvas,
  .camera-placeholder {
    height: auto;
    aspect-ratio: 564 / 225;
  }

  .scanner-entry {
    flex-direction: column;
  }

  .tiger-amount-entry {
    grid-template-columns: 1fr;
    align-items: stretch;
  }

  .tiger-amount-entry .scanner-add-action {
    width: 100%;
  }

  .scanner-entry__input {
    flex: 0 0 45px;
    width: 100%;
  }

  .scanner-add-action {
    width: 100%;
  }

  .scanner-summary {
    grid-template-columns: 1fr;
    gap: 14px;
    margin-top: 24px;
  }

  .scanner-dialog__actions {
    flex-direction: column-reverse;
    gap: 12px;
    padding: 28px 16px 24px !important;
  }

  .scanner-cancel-action,
  .scanner-submit-action {
    width: 100%;
    min-width: 0 !important;
  }

  .form-label {
    margin-bottom: 16px;
    font-size: 21px;
  }

  .rate-option {
    min-height: 58px;
    margin-bottom: 14px;
    padding: 9px 16px;
  }

  .rate-radio >>> .v-label {
    font-size: 16px;
  }

  .rate-radio >>> .v-input--selection-controls__input {
    width: 28px;
    height: 28px;
    margin-right: 18px;
  }

  .rate-radio >>> .v-input--selection-controls__input .v-icon {
    font-size: 28px;
  }

  .form-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .primary-action,
  .secondary-action {
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .primary-action,
  .secondary-action {
    transition: none !important;
  }
}
</style>
